// supabase/functions/_shared/clientBrand.ts
// The ONE place that decides a client's brand colors and logo.
//
// Pure: callers fetch the rows (the app with the browser client, nexus-mcp with
// the caller-scoped client) and hand them here. Shared by both runtimes, so it
// must stay free of Deno-only APIs and imports (src/services/brand/clientBrand.ts
// re-exports it, the same pattern as src/privacy/scrub.ts).
//
// Precedence, per the 2026-11-04 canonical load (migration 20261105003124):
// - palette: brand_profiles.color_palette (Canva brand kit; primary[0] is the
//   lead color) → brand_profiles.primary_colors → clients.brand.colorPalette /
//   clients.brand.primaryColor.
// - logo: brand_profiles.logo_url → the brand_assets logo whose
//   metadata.variant is "primary" → clients.brand.logoUrl.
// Logos are identified by brand_assets.metadata.variant, never by storage path.

export interface BrandColor {
  name?: string;
  hex: string;
}

export interface BrandPalette {
  primary: BrandColor[];
  secondary: BrandColor[];
  subPalettes: Record<string, BrandColor[]>;
  notes: string | null;
  source: string | null;
  canvaKitId: string | null;
  verifiedAt: string | null;
}

export interface BrandLogo {
  variant: string;
  url: string;
  storagePath: string | null;
}

export interface ClientBrand {
  palette: BrandPalette | null;
  /** primary[0].hex, or null when no palette is on file. */
  leadColor: string | null;
  logoUrl: string | null;
  /** Logo variants, "primary" first. */
  logos: BrandLogo[];
  paletteSource: "color_palette" | "primary_colors" | "clients.brand" | null;
  logoSource: "brand_profiles" | "brand_assets" | "clients.brand" | null;
}

export interface ClientBrandInputs {
  profile: {
    color_palette?: unknown;
    primary_colors?: unknown;
    logo_url?: string | null;
  } | null;
  /** brand_assets rows with asset_type = 'logo'. */
  logoAssets: { url?: string | null; metadata?: unknown }[];
  /** clients.brand jsonb. */
  clientBrand: unknown;
}

const HEX = /^#[0-9A-Fa-f]{6}$/;

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

/** Keeps entries that are {hex} with a valid #RRGGBB hex, or bare hex strings. */
function colors(v: unknown): BrandColor[] {
  if (!Array.isArray(v)) return [];
  const out: BrandColor[] = [];
  for (const c of v) {
    if (typeof c === "string") {
      if (HEX.test(c)) out.push({ hex: c });
      continue;
    }
    const o = obj(c);
    const hex = str(o?.hex);
    if (!hex || !HEX.test(hex)) continue;
    const name = str(o?.name);
    out.push(name ? { name, hex } : { hex });
  }
  return out;
}

function emptyPalette(primary: BrandColor[], secondary: BrandColor[] = []) {
  return {
    primary,
    secondary,
    subPalettes: {},
    notes: null,
    source: null,
    canvaKitId: null,
    verifiedAt: null,
  } satisfies BrandPalette;
}

function resolvePalette(
  inputs: ClientBrandInputs,
): [BrandPalette | null, ClientBrand["paletteSource"]] {
  const cp = obj(inputs.profile?.color_palette);
  const primary = colors(cp?.primary);
  if (cp && primary.length > 0) {
    const subPalettes: Record<string, BrandColor[]> = {};
    for (const [label, list] of Object.entries(obj(cp.sub_palettes) ?? {})) {
      const c = colors(list);
      if (c.length > 0) subPalettes[label] = c;
    }
    return [
      {
        primary,
        secondary: colors(cp.secondary),
        subPalettes,
        notes: str(cp.notes),
        source: str(cp.source),
        canvaKitId: str(cp.canva_kit_id),
        verifiedAt: str(cp.verified_at),
      },
      "color_palette",
    ];
  }

  const legacy = colors(inputs.profile?.primary_colors);
  if (legacy.length > 0) return [emptyPalette(legacy), "primary_colors"];

  const brand = obj(inputs.clientBrand);
  const cbp = obj(brand?.colorPalette);
  const lead = str(cbp?.primary) ?? str(brand?.primaryColor);
  if (lead && HEX.test(lead)) {
    const second = str(cbp?.secondary);
    return [
      emptyPalette(
        [{ hex: lead }],
        second && HEX.test(second) ? [{ hex: second }] : [],
      ),
      "clients.brand",
    ];
  }
  return [null, null];
}

function resolveLogos(assets: ClientBrandInputs["logoAssets"]): BrandLogo[] {
  const logos: BrandLogo[] = [];
  for (const a of assets) {
    const url = str(a.url);
    if (!url) continue;
    const m = obj(a.metadata);
    logos.push({
      variant: str(m?.variant) ?? "unlabelled",
      url,
      storagePath: str(m?.storage_path),
    });
  }
  // Stable: "primary" first, everything else in the order given.
  return logos
    .map((l, i) => ({ l, i }))
    .sort(
      (a, b) =>
        Number(b.l.variant === "primary") - Number(a.l.variant === "primary") ||
        a.i - b.i,
    )
    .map(({ l }) => l);
}

/** Resolves a client's brand colors and logo from already-fetched rows. */
export function resolveClientBrand(inputs: ClientBrandInputs): ClientBrand {
  const [palette, paletteSource] = resolvePalette(inputs);
  const logos = resolveLogos(inputs.logoAssets ?? []);

  let logoUrl = str(inputs.profile?.logo_url);
  let logoSource: ClientBrand["logoSource"] = logoUrl ? "brand_profiles" : null;
  if (!logoUrl) {
    logoUrl = logos.find((l) => l.variant === "primary")?.url ?? null;
    if (logoUrl) logoSource = "brand_assets";
  }
  if (!logoUrl) {
    logoUrl = str(obj(inputs.clientBrand)?.logoUrl);
    if (logoUrl) logoSource = "clients.brand";
  }

  return {
    palette,
    leadColor: palette?.primary[0]?.hex ?? null,
    logoUrl,
    logos,
    paletteSource,
    logoSource,
  };
}
